import { CallDto } from '@act/shared/data-transfer-objects';
import { useCallback, useState } from 'react';
import { TrackingProvider } from 'react-components/tracking';
import { useFeatureFlag } from 'redux/utils/feature-flags/featureFlagHook.hook';
import { ActiveCallMenuAnsweredView } from './ActiveCallMenuAnsweredView';
import { ActiveCallMenuWaitingPanel } from './ActiveCallMenuWaitingPanel';
import { useActiveCallAddParticipant } from './useActiveCallAddParticipant.hook';
import { useActiveCallConnections } from './useActiveCallConnections.hook';
import { useActiveCallMenuActions } from './useActiveCallMenuActions.hook';

export interface ActiveCallMenuProps {
  call: CallDto;
  showConnectionBorders?: boolean;
  verticalCards?: boolean;
}

/**
 * In-call controls for adding participants, hold/merge, and leaving the call.
 *
 * @param props Call and layout options.
 * @returns Active call menu UI.
 */
export const ActiveCallMenu = ({
  call: initialCall,
  showConnectionBorders = false,
  verticalCards = false,
}: ActiveCallMenuProps) => {
  const resourcingCallsFF = useFeatureFlag('three-way-resourcing-calls');

  const [isAddingConnection, setIsAddingConnection] = useState(false);
  const [isAssigningNumber, setIsAssigningNumber] = useState(false);
  const [selectedCallType, setSelectedCallType] = useState('internal');

  const {
    call,
    allConnections,
    connections,
    guideConnection,
    guideHasAnswered,
    isMemberKnown,
    mainConnection,
    isMainConnectionOnHold,
    activeConnectionsCount,
    shouldShowAddCallerButton,
  } = useActiveCallConnections(initialCall);

  const addParticipant = useActiveCallAddParticipant(
    call,
    isMemberKnown,
    mainConnection,
    isAddingConnection
  );

  const resetAddParticipantUi = useCallback(() => {
    addParticipant.resetAddParticipantForm();
    setIsAddingConnection(false);
  }, [addParticipant.resetAddParticipantForm]);

  const {
    isAddingGuideToCall,
    isMergingCalls,
    isHangingUpGuide,
    handleMergeCall,
    handleLeaveCall,
    submitAddGuide,
  } = useActiveCallMenuActions(
    call,
    allConnections,
    guideConnection,
    resetAddParticipantUi
  );

  const onSubmitAddCaller = () => {
    void addParticipant.handleSubmit(data =>
      addParticipant.onSubmitAddCaller(data, submitAddGuide)
    )();
  };

  return (
    <TrackingProvider contextName="active-call-menu">
      {!guideHasAnswered ? (
        <ActiveCallMenuWaitingPanel />
      ) : (
        <ActiveCallMenuAnsweredView
          call={call}
          connections={connections}
          activeConnectionsCount={activeConnectionsCount}
          showConnectionBorders={showConnectionBorders}
          verticalCards={verticalCards}
          isMainConnectionOnHold={isMainConnectionOnHold}
          isMemberKnown={isMemberKnown}
          shouldShowAddCallerButton={shouldShowAddCallerButton}
          isAddingConnection={isAddingConnection}
          setIsAddingConnection={setIsAddingConnection}
          isAssigningNumber={isAssigningNumber}
          setIsAssigningNumber={setIsAssigningNumber}
          mainConnection={mainConnection}
          resourcingCallsFF={resourcingCallsFF}
          selectedCallType={selectedCallType}
          setSelectedCallType={setSelectedCallType}
          addParticipant={addParticipant}
          isAddingGuideToCall={isAddingGuideToCall}
          isMergingCalls={isMergingCalls}
          isHangingUpGuide={isHangingUpGuide}
          guideConnection={guideConnection}
          onMergeCall={handleMergeCall}
          onLeaveCall={handleLeaveCall}
          onSubmitAddCaller={onSubmitAddCaller}
        />
      )}
    </TrackingProvider>
  );
};
